import eq from "lodash-es/eq"

import { THEME } from "@/3514/theme"
import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import {
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatFormErrorVariant as EFormErrorVariant,
    ECoachingChatMatrixFeedbackModalChartQuadrantKey as EMatrixChartQuadrantKey,
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatSummaryCardMode as EMode
} from "../types"

const classes = {
    entity: {
        section: (): string => "flex flex-col gap-y-[15px]",
        spinner: ({ mode }: { mode: EEntityMode }): string =>
            buildCssClassName({ "translate-y-[20px]": mode === EEntityMode.Light })
    },
    form: {
        seedQuestion: {
            form: ({
                isError,
                isActive,
                isLoading
            }: {
                isError: boolean
                isActive: boolean
                isLoading: boolean
            }): string =>
                buildCssClassName(
                    [
                        "relative",
                        "flex",
                        "flex-auto",
                        "flex-col",
                        "border-[1px]",
                        "rounded-[10px]",
                        "relative",
                        "max-h-full",
                        "px-[12px]",
                        "pt-[10px]",
                        "pb-[30px]"
                    ],
                    getTransitionTwClasses("colors", 200),
                    [isError && [isActive ? "bg-red-500 border-red-400" : "bg-white border-red-200"]],
                    [isActive && !isError && "bg-blue-600 border-blue-500"],
                    [!isActive && !isError && "bg-white border-blue-400"],
                    [isLoading && "bg-white border-blue-400"]
                ),
            loader: (): string => "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            title: ({ isInactive, isError }: { isInactive: boolean; isError: boolean }): string =>
                buildCssClassName(
                    "relative",
                    "md:text-wrap",
                    "pt-[5px]",
                    "px-[8px]",
                    "pb-[15px]",
                    isError ? "selection:bg-red-300" : "selection:bg-blue-300",
                    [
                        isInactive && [
                            "after:content-['']",
                            "after:absolute",
                            "after:block",
                            "after:w-[calc(100%-16px)]",
                            "after:mx-auto",
                            "after:bottom-[0px]",
                            "after:left-0",
                            "after:right-0",
                            "after:h-[1px]",
                            [isError ? "after:bg-red-300" : "after:bg-blue-400"]
                        ]
                    ]
                ),
            button: (isButtonVisible: boolean): string => buildCssClassName(!isButtonVisible && "hidden"),
            textarea: ({ isError }: { isError: boolean }): string =>
                buildCssClassName(
                    "h-full",
                    "!cursor-auto",
                    "bg-white",
                    "caret-gray-100",
                    "!p-[8px]",
                    isError ? "selection:bg-red-300" : "selection:bg-blue-300"
                )
        },
        seedQuestions: {
            container: ({ areInsightsEnabled }: { areInsightsEnabled: boolean }): string =>
                buildCssClassName("flex flex-col gap-y-[15px]", areInsightsEnabled || "pt-[10px] md:pt-[0px]"),
            heading: (): string =>
                buildCssClassName(
                    "flex",
                    "flex-col",
                    "md:flex-row",
                    "px-[5px]",
                    "md:px-[10px]",
                    "w-full",
                    "items-start",
                    "md:items-center",
                    "gap-y-[5px]",
                    "md:gap-y-[0px]",
                    "md:justify-between"
                ),
            title: (): string => "!mb-[0px]",
            questionsWrapper: (): string => "flex flex-col gap-y-[15px]"
        }
    },
    conversation: {
        banner: {
            container: (): string => "absolute h-full w-full",
            contentWrapper: (errorVariant: EFormErrorVariant): string =>
                buildCssClassName(
                    "absolute",
                    "top-[40%]",
                    "left-1/2",
                    "-translate-x-1/2",
                    "-translate-y-1/2",
                    "flex",
                    "flex-col",
                    "gap-y-[20px]",
                    "w-full",
                    "max-w-[230px]",
                    "md:max-w-full",
                    [eq(errorVariant, EFormErrorVariant.Api) && "min-w-[80%] top-[50%] md:top-[50%]"],
                    [eq(errorVariant, EFormErrorVariant.ContentViolation) && "min-w-full top-[50%] md:top-[50%]"]
                ),
            contentHeadingWrapper: ({ isError }: { isError: boolean }): string =>
                buildCssClassName("flex flex-col", isError && "gap-y-[10px]"),
            title: (): string => "!mb-[0px] select-none",
            hint: (): string => "select-none",
            formErrorBox: (errorVariant: EFormErrorVariant): string =>
                buildCssClassName(
                    "[pointer-events:auto] mx-auto",
                    eq(errorVariant, EFormErrorVariant.ContentViolation) && "max-w-[95%] md:max-w-[620px]",
                    eq(errorVariant, EFormErrorVariant.Api) && "max-w-[80%] md:max-w-[400px]"
                ),
            button: (): string => "[pointer-events:auto] select-none"
        },
        violationFeedbackOpener: {
            wrapper: (): string => "group inline [pointer-events:auto] [cursor:pointer]",
            action: ({ isAccent, isWhite }: { isAccent: boolean; isWhite: boolean }): string =>
                buildCssClassName(
                    "underline",
                    "decoration-[1px]",
                    "underline-offset-[3px]",
                    "group-focus:underline-offset-[5px]",
                    "group-hover:underline-offset-[5px]",
                    isWhite && "decoration-white",
                    isAccent && "decoration-accent"
                )
        },
        closedIndicator: {
            container: (isVisible: boolean): string =>
                buildCssClassName(
                    [
                        "pt-[10px]",
                        "pb-[20px]",
                        "px-[20px]",
                        "flex",
                        "items-center",
                        "gap-x-[10px]",
                        "w-full",
                        "bg-transparent"
                    ],
                    isVisible || "hidden"
                ),
            hr: (): string => "bg-white w-full h-[1px] m-[0px]",
            text: (): string => "text-shadow-chat-completed min-w-max"
        },
        matrixOpener: {
            container: (): string => "px-[20px] pb-[20px] w-full",
            wrapperInsertionStyles: (): object => ({
                "&:hover, &:focus": {
                    p: {
                        color: THEME.COLORS.blue[100]
                    }
                },
                p: {
                    fontSize: `${THEME.FONT_SIZES[13]}px`,
                    lineHeight: `${THEME.LINE_HEIGHTS[20]}px`,
                    fontWeight: THEME.FONT_WEIGHTS[300],
                    color: THEME.COLORS.blue[500],
                    marginBottom: 0,
                    textAlign: "center"
                },
                "p > strong": { fontWeight: THEME.FONT_WEIGHTS[800] }
            }),
            wrapper: ({ isMatrixFeedbackCompleted }: { isMatrixFeedbackCompleted: boolean }): string =>
                buildCssClassName(
                    "!outline-none",
                    "select-none",
                    "py-[9px]",
                    "px-[20px]",
                    "w-full",
                    "max-w-full",
                    "rounded-[5px]",
                    "hover:bg-blue-400",
                    "focus:bg-blue-400",
                    "inline-block",
                    "flex",
                    "flex-col",
                    "md:flex-row",
                    "items-center",
                    "justify-center",
                    "gap-y-[5px]",
                    "md:gap-y-[0px]",
                    "md:gap-x-[5px]",
                    [
                        isMatrixFeedbackCompleted
                            ? "[pointer-events:none] [cursor:default] bg-blue-200/60"
                            : "cursor-pointer bg-blue-200"
                    ],
                    getTransitionTwClasses("all", 100)
                ),
            completedLabel: (): string => "translate-y-[1px]",
            notCompletedLabel: (): string => buildCssClassName("text-pretty", getTransitionTwClasses("all", 100))
        },
        container: {
            container: ({ isChatLocked }: { isChatLocked: boolean }): string =>
                buildCssClassName(
                    "rounded-[10px]",
                    "flex",
                    "flex-col",
                    "justify-end",
                    "bg-chat-active",
                    "w-full",
                    "overflow-y-auto",
                    "relative",
                    "h-[500px]",
                    "py-[5px]",
                    [isChatLocked && "[pointer-events:none]"]
                ),
            spinner: "m-auto"
        },
        errorBox: {
            container: (errorVariant: EConversationErrorVariant): string =>
                buildCssClassName(
                    eq(errorVariant, EConversationErrorVariant.AiStuck)
                        ? [
                              "flex",
                              "gap-x-[15px]",
                              "relative",
                              "max-w-[580px]",
                              "self-center",
                              "mx-auto",
                              "w-[calc(100%-20px)]",
                              "md:w-full",
                              "rounded-[10px]",
                              "bg-[rgba(255,255,255,0.40)]",
                              "pt-[12px]",
                              "px-[40px]",
                              "pb-[15px]"
                          ]
                        : ["max-w-[calc(100%-60px)]", "w-full", "my-[7px]", "mx-auto", "[position:stickdsay]"]
                ),
            textWrapper: (errorVariant: EConversationErrorVariant): string =>
                buildCssClassName(
                    eq(errorVariant, EConversationErrorVariant.AiStuck) && "flex flex-col items-center w-full"
                ),
            titleWrapper: (errorVariant: EConversationErrorVariant): string =>
                buildCssClassName(
                    eq(errorVariant, EConversationErrorVariant.AiStuck) &&
                        "flex gap-x-[5px] items-center justify-center"
                ),
            text: (): string => "inline selection:bg-gray-400",
            textActionWrapper: (): string => "mx-auto group",
            textAction: (isActionAffected: boolean): string =>
                buildCssClassName(
                    "underline underline-accent cursor-pointer select-none",
                    isActionAffected && "underline-offset-[5px]",
                    getTransitionTwClasses()
                )
        },
        feed: {
            container: ({ isAiStuck }: { isAiStuck: boolean }): string =>
                buildCssClassName("overflow-y-auto no-scrollbar relative", isAiStuck && "h-full"),
            feed: ({ isAiStuck, areMessagesPresent }: { isAiStuck: boolean; areMessagesPresent: boolean }): string =>
                buildCssClassName(
                    "w-full justify-end overflow-y-scroll no-scrollbar",
                    isAiStuck
                        ? [
                              areMessagesPresent
                                  ? "mt-[40px] h-max"
                                  : "[height:100%] mt-[0px] flex flex-col justify-center"
                          ]
                        : "mt-[40px] h-max"
                )
        },
        heading: {
            container: (): string =>
                buildCssClassName([
                    "absolute",
                    "w-full",
                    "rounded-tl-[10px]",
                    "rounded-tr-[10px]",
                    "top-[0px]",
                    "left-[0px]",
                    "bg-chat-title",
                    "flex",
                    "gap-x-[10px]",
                    "pt-[15px]",
                    "pb-[10px]",
                    "px-[15px]",
                    "backdrop-blur-[2px]",
                    "z-[1]"
                ])
        },
        input: {
            field: ({ isChatLocked }: { isChatLocked: boolean }): string =>
                buildCssClassName(
                    "bg-white",
                    "rounded-[10px]",
                    "mt-[5px]",
                    "mx-[10px]",
                    "mb-[10px]",
                    "h-auto",
                    "sticky",
                    "border-[1px]",
                    "border-blue-400",
                    "focus-within:border-blue-600",
                    getTransitionTwClasses("all", 200),
                    [isChatLocked ? "opacity-[0.5] pointer-events-none" : "opacity-1"]
                ),
            container: (): string =>
                buildCssClassName(
                    [
                        "rounded-[8px]",
                        "flex",
                        "flex-col",
                        "gap-y-[12px]",
                        "mb-[0px]",
                        "pt-[12px]",
                        "pr-[8px]",
                        "pb-[8px]",
                        "pl-[15px]",
                        "border-[1px]",
                        "border-blue-400",
                        "!border-t-transparent",
                        "focus:border-blue-600",
                        "active:border-blue-600",
                        "focus-within:border-blue-600",
                        "hover:border-blue-600",
                        "active:border-blue-600",
                        "h-auto"
                    ],
                    getTransitionTwClasses("all", 150)
                ),
            infoBox: (): string => "flex ml-auto items-end gap-x-[8px] h-[33px]",
            input: (): string =>
                "caret-gray-300 selection:bg-blue-300 text-pretty spelling-error:bg-red-100 spelling:bg-red-100",
            hint: (): string => "pb-[2px] select-none pointer-events-none",
            infoBoxButton: ({ isDisabled, isStale }: { isDisabled: boolean; isStale: boolean }): string =>
                buildCssClassName(
                    "border-[1px]",
                    "border-transparent",
                    "focus:border-accent",
                    "pl-[7px]",
                    "h-full",
                    "min-w-[33px]",
                    "max-w-[33px]",
                    "!rounded-[5px]",
                    "relative",
                    "after:absolute",
                    "after:z-[-1]",
                    "after:inset-[-8px]",
                    [
                        isDisabled && "!bg-gray-300 pointer-events-none",
                        !isDisabled && "!bg-accent hover:[scale:1.05] focus:[scale:1.05]",
                        isStale && "!bg-transparent pointer-events-none"
                    ]
                ),
            icon: (): string => "cursor-pointer"
        },
        owls: {
            default: {
                wrapper: ({ fill }: { fill: "blue-500" | "blue-600" }): string =>
                    buildCssClassName(
                        "pt-[17px] px-[3px] rounded-[20px] min-w-[30px] h-[30px] overflow-hidden",
                        eq(fill, "blue-500") && "bg-blue-500",
                        eq(fill, "blue-600") && "bg-blue-600"
                    ),
                img: (): string => "relative bottom-[10px]"
            },
            guide: "max-h-[120px] max-w-[120px]"
        },
        message: {
            container: ({
                author,
                isNotInteractive,
                isLight
            }: {
                author: EMessageAuthor
                isNotInteractive: boolean
                isLight: boolean
            }): string =>
                buildCssClassName(
                    "flex w-full bg-transparent py-[7px] relative",
                    getTransitionTwClasses("colors"),
                    !isNotInteractive && "hover:!bg-blue-200/[0.3] focus:!bg-blue-200/[0.3]",
                    eq(author, EMessageAuthor.User)
                        ? [isLight ? "px-[20px]" : "pl-[55px] md:pl-[40px] pr-[20px] justify-end justify-end"]
                        : [isLight ? "px-[20px]" : "pl-[20px] pr-[55px] md:pr-[40px]", !isNotInteractive && "group"]
                ),
            messageRow: ({ author }: { author: EMessageAuthor }): string =>
                buildCssClassName(
                    "flex gap-x-[5px] items-end",
                    eq(author, EMessageAuthor.User) ? "ml-auto" : ["mr-auto", "flex-row-reverse"]
                ),
            messageWrapper: (): string => "flex items-center relative gap-x-[5px]",
            message: ({
                author,
                appearance = "default"
            }: {
                author: EMessageAuthor
                appearance?: "default" | "progressing"
            }): string =>
                buildCssClassName(
                    [
                        "rounded-[20px]",
                        "selection:bg-blue-300",
                        "whitespace-pre-line",
                        "text-pretty",
                        "py-[10px]",
                        "px-[15px]",
                        "border-[1px]"
                    ],
                    getTransitionTwClasses("all"),
                    appearance === "default"
                        ? [
                              "w-auto",
                              eq(author, EMessageAuthor.User)
                                  ? "bg-blue-200/60 max-w-[385px] border-transparent"
                                  : "bg-transparent max-w-[445px] border-blue-200"
                          ]
                        : ["w-full", "max-w-[445px]", "bg-transparent", "border-transparent"]
                ),
            messageFeedbackButton: (): string =>
                buildCssClassName(
                    [
                        "w-[25px]",
                        "h-[25px]",
                        "items-center",
                        "justify-center",
                        "cursor-pointer",
                        "p-[5px]",
                        "bg-blue-600",
                        "mx-[5px]",
                        "md:mx-[15px]",
                        "rounded-[5px]",
                        "absolute",
                        "left-[calc(100%+5px)]",
                        "md:relative",
                        "md:left-[0px]",
                        "border-[1px]",
                        "border-gray-400",
                        "hidden",
                        "hover:border-gray-300",
                        "focus:border-gray-300",
                        "group-hover:flex",
                        "group-focus:flex"
                    ],
                    getTransitionTwClasses()
                ),
            svgElem: (): string => "pointer-events-none absolute inset-0 h-full w-full",
            retrierContainer: (): string => "flex items-center gap-x-[10px] self-start mt-[5px] mb-[-8px]",
            retrierButton: (): string =>
                buildCssClassName(
                    "flex",
                    "items-center",
                    "justify-center",
                    "bg-transparent",
                    "rounded-[5px]",
                    "h-[25px]",
                    "w-[25px]",
                    "border-[1px]",
                    "border-blue-300",
                    "hover:bg-white",
                    "hover:border-transparent",
                    "focus-visible:bg-white",
                    "focus-visible:border-transparent",
                    getTransitionTwClasses("all", 200)
                )
        },
        summary: {
            generatingSummaryBox: "bg-blue-200 rounded-[10px] py-[10px] px-[20px] my-[7px] mx-[40px] w-fit mx-auto",
            summaryWrapper: "flex flex-col"
        },
        summaryCards: {
            parentContainer: (mode: EMode, isDesktop: boolean): string =>
                buildCssClassName(
                    "[display:grid]",
                    eq(mode, EMode.BuildInInsights) && [
                        [isDesktop ? "grid-cols-12 gap-x-[8px] gap-y-[8px]" : "grid-cols-1 gap-y-[8px]"]
                    ],
                    eq(mode, EMode.BuildInChat) && [
                        "grid-cols-12",
                        "gap-y-[8px]",
                        "md:gap-y-[8px]",
                        "md:gap-x-[8px]",
                        "md:flex-row",
                        "pt-[20px]",
                        "px-[15px]",
                        "pb-[10px]"
                    ]
                )
        },
        summaryCard: {
            card: (mode: EMode, cardsAmount: number): string =>
                buildCssClassName(
                    [
                        "col-span-12",
                        eq(cardsAmount, 1) && "xl:col-span-12",
                        eq(cardsAmount, 2) && "xl:col-span-6",
                        eq(cardsAmount, 3) && "xl:col-span-4",
                        cardsAmount > 3 && "xl:col-span-6"
                    ],
                    eq(mode, EMode.BuildInInsights) && ["relative", "min-h-[45px]"],
                    eq(mode, EMode.BuildInChat) && [
                        "shadow-summaryCardItem",
                        "[background:rgba(248,248,254,0.05)]",
                        "backdrop-blur-[12px]",
                        "rounded-[10px]",
                        "border-[1px]",
                        "border-blue-200"
                    ]
                ),
            cardBody: (mode: EMode, isActionAffected: boolean): string =>
                buildCssClassName(
                    eq(mode, EMode.BuildInInsights) && [
                        "absolute",
                        "inset-0",
                        "pt-[11px]",
                        "px-[15px]",
                        "rounded-[10px]",
                        "pb-[11px]",
                        "overflow-hidden",
                        "border-[1px]",
                        "border-blue-500",
                        "flex",
                        "flex-col",
                        "bg-blue-600",
                        "max-h-max",
                        // "min-h-max",
                        "lg:[max-height:initial]",
                        "lg:[min-height:initial]",
                        getTransitionTwClasses("[height]", 100, "ease-in"),
                        isActionAffected ? ["z-[11]", "shadow-summaryCardItem"] : ["z-[10]"]
                    ],
                    eq(mode, EMode.BuildInChat) && [
                        "pt-[11px]",
                        "pb-[15px]",
                        "px-[15px]",
                        "gap-y-[8px]",
                        "flex",
                        "flex-col"
                    ]
                ),
            cardHeading: (mode: EMode): string =>
                buildCssClassName(
                    "flex",
                    "select-none",
                    "w-full",
                    eq(mode, EMode.BuildInInsights) && "justify-between items-start",
                    eq(mode, EMode.BuildInChat) && "justify-start items-center gap-x-[5px]"
                ),
            cardTitleLabel: (mode: EMode, isCardOpened: boolean): string =>
                buildCssClassName(
                    eq(mode, EMode.BuildInChat) && [],
                    eq(mode, EMode.BuildInInsights) && [
                        "max-w-fit",
                        isCardOpened ? "[word-break:break-word]" : "truncate block line-clamp-1"
                    ]
                ),
            cardCopyContainer: (): string => "h-[30px] w-auto flex items-center gap-x-[10px]",
            cardIcon: (mode: EMode, isVisible: boolean): string =>
                buildCssClassName(eq(mode, EMode.BuildInInsights) && [isVisible ? "z-[20]" : "hidden"]),
            cardDate: (mode: EMode): string => buildCssClassName(eq(mode, EMode.BuildInInsights) && "w-max"),
            cardContentWrapper: (mode: EMode, isVisible: boolean): string =>
                buildCssClassName(eq(mode, EMode.BuildInInsights) && [isVisible ? "mt-[7px]" : "[visibility:hidden]"]),
            cardContentEditor: (): string => "select-none"
        },
        typingIndicator: {
            holder: ({ isVisible, isDimmed }: { isVisible: boolean; isDimmed: boolean }): string =>
                buildCssClassName(
                    [
                        isVisible
                            ? "flex items-end justify-center gap-x-[5px] py-[7px] w-fit translate-x-[20px]"
                            : "hidden"
                    ],
                    [isDimmed && "blur-[2px] opacity-50"],
                    getTransitionTwClasses("all", 100)
                ),
            dotsContainer: (): string =>
                buildCssClassName(
                    "flex",
                    "items-center",
                    "self-start",
                    "justify-center",
                    "flex-none",
                    "h-[40px]",
                    "w-[65px]",
                    "rounded-[20px]",
                    "bg-transparent",
                    "translate-y-[5px]"
                ),
            dotsWrapper: (): string =>
                "flex items-center justify-center h-[20px] gap-x-[6px] px-[6px] bg-transparent rounded-lg",
            dot: (index: number): string =>
                buildCssClassName(
                    "w-[8px] h-[8px] bg-blue-300 rounded-full animate-chatTypingBounce-dot",
                    eq(index, 1) && "animate-delay-150",
                    eq(index, 2) && "animate-delay-300"
                ),
            delayerWrapper: (): string => "flex flex-col items-start justify-start gap-y-[5px]",
            delayerText: (): string => "translate-x-[15px] mb-[10px]"
        }
    },
    insights: {
        container: "flex flex-col gap-y-[15px] mb-[45px] lg:mb-[65px]",
        titleWrapper: "flex items-center gap-x-[10px] px-[5px] md:px-[10px]",
        title: "!mb-[0px]",
        incompleteWrapper: buildCssClassName(
            "flex",
            "flex-col",
            "gap-y-[7px]",
            "bg-red-500",
            "rounded-[10px]",
            "border-[1px]",
            "border-red-200",
            "border-dashed",
            "px-[15px]",
            "pt-[10px]",
            "pb-[20px]"
        ),
        incompleteTitle: "flex items-center gap-x-[5px]",
        incompleteHint: "inline"
    },
    modals: {
        abandonment: {
            modalBody: buildCssClassName([
                "w-[90%]",
                "md:w-full",
                "md:max-w-[440px]",
                "py-[35px]",
                "px-[30px]",
                "h-full",
                "max-h-[270px]",
                "min-h-fit",
                "bg-white",
                "rounded-[10px]",
                "flex",
                "flex-col"
            ]),
            contentContainer: "flex flex-col gap-y-[25px] mt-[8px]",
            buttonSet: buildCssClassName([
                "flex",
                "flex-col",
                "md:flex-row",
                "items-center",
                "justify-around",
                "gap-y-[10px]",
                "md:gap-y-[10px]",
                "md:gap-x-[10px]",
                "pb-[1px]"
            ])
        },
        guide: {
            modalOpener: "flex gap-x-[5px]",
            modalHeader: "pt-[20px] md:pt-[0px]",
            modalBody: buildCssClassName(
                "flex",
                "flex-col",
                "max-w-[340px]",
                "w-full",
                "max-h-[90%]",
                "md:max-w-[620px]",
                "py-[35px]",
                "px-[20px]",
                "lg:px-[30px]",
                "bg-white",
                "rounded-[10px]",
                "relative",
                "h-screen",
                "md:h-auto",
                "overflow-y-auto",
                "no-scrollbar"
            ),
            modalBodyContainer: buildCssClassName(
                "flex",
                "flex-col",
                "[flex:1_1_auto]",
                "no-scrollbar",
                "overscroll-none",
                "touch-auto",
                "pt-[8px]",
                "md:pt-[0px]",
                "md:mb-[0px]"
            ),
            modalBodyHeading: "flex items-start mb-[8px] self-center mt-[10px]",
            modalBodyHeadingCaption: buildCssClassName(
                "py-[10px]",
                "px-[20px]",
                "bg-blue-400",
                "rounded-[10px]",
                "w-full",
                "max-w-[170px]",
                "md:max-w-[215px]"
            ),
            modalBodyWrapper: "flex flex-col",
            modalBodyTextList: buildCssClassName(
                "pl-[24px]",
                "mx-[5px]",
                "md:mx-[20px]",
                "mb-[28px]",
                "flex",
                "flex-col",
                "gap-y-[8px]",
                "[list-style-type:decimal]"
            ),
            modalBodyTextListItemTitle: "mb-[2px]",
            modalBodyDisclaimer: buildCssClassName(
                "px-[5px]",
                "md:px-[20px]",
                "pt-[20px]",
                "mb-[28px]",
                "flex",
                "flex-col",
                "gap-y-[8px]",
                "border-blue-500",
                "border-t-[1px]"
            ),
            modalBodyDisclaimerLoader: "mb-[20px]",
            modalBodyDisclaimerTitle: "pl-[5px]",
            modalBodyDisclaimerList: "mx-[20px]",
            modalBodyContactContainer: buildCssClassName(
                "[display:grid]",
                "gap-[20px]",
                "items-center",
                "pt-[30px]",
                "border-t-[1px]",
                "border-blue-500"
            )
        },
        matrix: {
            modalBody: buildCssClassName(
                "pt-[60px]",
                "px-[20px]",
                "pb-[40px]",
                "md:pt-[40px]",
                "md:px-[30px]",
                "lg:py-[40px]",
                "lg:px-[45px]",
                "max-w-[95%]",
                "md:max-w-[645px]",
                "w-full",
                "max-[767px]:overflow-y-scroll",
                "max-[767px]:no-scrollbar",
                "overflow-y-scroll",
                "no-scrollbar",
                "max-h-[90%]",
                "bg-white",
                "rounded-[10px]"
            ),
            modalBodyContainer: "flex flex-col items-center gap-y-[30px]",
            modalHeaderNodeContainer: "flex gap-x-[5px] items-end justify-center pb-[30px]",
            modalHeaderNode: "px-[20px] py-[9px] rounded-[40px] bg-blue-400",
            modalBodyContainerMessageTextInsertion: {
                p: {
                    fontSize: `${THEME.FONT_SIZES[16]}px`,
                    lineHeight: `${THEME.LINE_HEIGHTS[24]}px`,
                    fontWright: THEME.FONT_WEIGHTS[300],
                    color: THEME.COLORS.gray[100],
                    marginBottom: 0,
                    textAlign: "center"
                },
                "p > strong": { fontWeight: THEME.FONT_WEIGHTS[800] }
            },
            modalBodyChartWrapper: "flex flex-col md:gap-y-[40px] w-full",
            modalBodyWrapper: "flex flex-col items-center gap-y-[30px] lg:gap-y-[40px] lg:mb-[10px]",
            modalBodyWrapperFormWrapper: "flex flex-col gap-y-[10px] w-full",
            modalBodyWrapperForm: buildCssClassName(
                "w-full",
                "px-[12px]",
                "pt-[10px]",
                "pb-[15px]",
                "rounded-[10px]",
                "border-blue-400",
                "border-[1px]",
                "bg-white"
            ),
            modalBodyWrapperFormLabel: buildCssClassName(
                "pt-[5px]",
                "pb-[15px]",
                "px-[8px]",
                "relative",
                "after:absolute",
                "after:w-[calc(100%-16px)]",
                "after:h-[1px]",
                "after:bottom-[0px]",
                "after:left-1/2",
                "after:-translate-x-1/2",
                "after:bg-blue-400"
            ),
            modalBodyWrapperFormInput: "!p-[8px] selection:bg-blue-400",
            modalBodyWrapperButtonWrapper: "flex flex-col items-center justify-center gap-y-[10px]",
            modalBodyWrapperChart: {
                container: (): string => "flex items-center justify-center max-w-[400px] self-center w-full",
                wrapper: (): string =>
                    buildCssClassName(
                        "relative",
                        "select-none",
                        "overflow-hidden",
                        "w-full",
                        "aspect-[1/0.8]",
                        "touch-none",
                        "select-none",
                        "overscroll-contain"
                    ),
                overlay: (): string => "absolute inset-0 bg-white",
                axisContainer: (): string => "absolute inset-0 pointer-events-none",
                axisY: (): string => "absolute bg-gray-23 w-[1px]",
                axisX: (): string => "absolute bg-gray-23 h-[1px]",
                rect: ({ key }: { key: EMatrixChartQuadrantKey }): string =>
                    buildCssClassName("absolute", "pointer-events-none", "[will-change:opacity]", [
                        eq(key, EMatrixChartQuadrantKey.QUADRANT_1) && "bg-green-300/50",
                        eq(key, EMatrixChartQuadrantKey.QUADRANT_2) && "bg-yellow-300/50",
                        eq(key, EMatrixChartQuadrantKey.QUADRANT_3) && "bg-red-300/50",
                        eq(key, EMatrixChartQuadrantKey.QUADRANT_4) && "bg-yellow-300/50"
                    ]),
                cell: (): string =>
                    buildCssClassName(
                        "absolute",
                        "pointer-events-none",
                        "rounded-[5px]",
                        "border-transparent",
                        "border-[1px]",
                        "focus-visible:border-gray-23",
                        getTransitionTwClasses("colors", 100)
                    ),
                draggerContainer: (): string =>
                    buildCssClassName(
                        "absolute",
                        "translate-x-[-50%]",
                        "translate-y-[-50%]",
                        "z-10",
                        "grid",
                        "place-items-center",
                        "outline-none"
                    ),
                draggerWrapper: (): string => "absolute touch-none ![cursor:auto]",
                dragger: (): string =>
                    buildCssClassName(
                        "rounded-full",
                        "shadow-lg",
                        "cursor-grab",
                        "active:cursor-grabbing",
                        "outline",
                        "outline-blue-200/60",
                        "will-change-transform"
                    )
            }
        }
    }
} as const

export { classes as coachingChatStyles }
